import {
  type SECRGrandTotals,
  type SECRReportResultCategories,
} from '@shared/api/types';
import { formatNumber } from '@shared/lib/utils';
import React from 'react';
import { EMISSION_SCOPES } from '../constants';

interface SECRReportCombinedTableProps {
  categories: SECRReportResultCategories;
  totals: SECRGrandTotals;
  currentYearLabel: string;
}

const fmt = (n: number | null | undefined) =>
  n != null && !isNaN(n) ? formatNumber(n) : '-';

const fmtKwh = (
  kwh: number | null | undefined,
  tonnes: number | null | undefined
) => {
  if (kwh != null && !isNaN(kwh)) return formatNumber(kwh);
  if (tonnes === 0) return '0';
  return '-';
};

export function SECRReportCombinedTable({
  categories,
  totals,
  currentYearLabel,
}: SECRReportCombinedTableProps) {
  return (
    <div className="overflow-hidden rounded-lg border shadow-sm">
      <table className="w-full border-collapse">
        <thead>
          <tr className="bg-muted/50 border-b">
            <th className="p-3 text-left text-sm font-semibold">
              Energy source
            </th>
            <th className="border-l p-3 text-right text-sm font-semibold">
              Emissions (tCO₂e)
            </th>
            <th className="border-l p-3 text-right text-sm font-semibold">
              Energy (kWh)
            </th>
          </tr>
          <tr className="border-b text-xs text-muted-foreground">
            <th className="px-3 py-2 text-left font-normal">
              {currentYearLabel}
            </th>
            <th className="border-l px-3 py-2 text-right font-normal" />
            <th className="border-l px-3 py-2 text-right font-normal" />
          </tr>
        </thead>

        <tbody>
          {EMISSION_SCOPES.map(({ label, rows }) => (
            <React.Fragment key={label}>
              <tr className="bg-muted/40 border-b">
                <td
                  colSpan={3}
                  className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground"
                >
                  {label}
                </td>
              </tr>
              {rows.map(({ key, label: rowLabel, icon }) => {
                const cat = categories[key];
                return (
                  <tr
                    key={key}
                    className="hover:bg-muted/20 border-b transition-colors last:border-0"
                  >
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        {icon && <span className="shrink-0">{icon}</span>}
                        <span className="text-sm">{rowLabel}</span>
                      </div>
                    </td>
                    <td className="border-l px-3 py-2.5 text-right text-sm font-medium tabular-nums">
                      {fmt(cat?.tonnes)}
                    </td>
                    <td className="border-l px-3 py-2.5 text-right text-sm font-medium tabular-nums">
                      {fmtKwh(cat?.kwh, cat?.tonnes)}
                    </td>
                  </tr>
                );
              })}
            </React.Fragment>
          ))}
        </tbody>

        <tfoot className="border-t-2">
          <tr className="bg-muted/40">
            <td className="px-3 py-2.5 text-sm font-semibold">Total</td>
            <td className="border-l px-3 py-2.5 text-right text-sm font-semibold tabular-nums">
              {fmt(totals.tonnes)}
            </td>
            <td className="border-l px-3 py-2.5 text-right text-sm font-semibold tabular-nums">
              {fmtKwh(totals.kwh, totals.tonnes)}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
